import { useEffect, useState } from 'react'
import { EnvelopeIcon, UserIcon } from '@heroicons/react/24/solid'
import type { UserInfo } from 'firebase/auth'
import { cn } from '~/src/lib/utils'
import useAuthUser from '~/src/hooks/useAuthUser'
import LoaderIcon from '~/src/components/svg/LoaderIcon'
import AppleIcon from '~/src/components/svg/AppleIcon'
import GoogleIcon from '~/src/components/svg/GoogleIcon'
import {
  AppleProvider,
  EmailAuthProvider,
  GoogleAuthProvider,
  SupportedProviderId,
} from '~/src/lib/firebase/firebaseAuth'

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  providerId?: SupportedProviderId
  size?: 'sm' | 'md' | 'lg'
  isGoogleAuth?: boolean
  providerUserInfo?: UserInfo
  providerData?: UserInfo[]
  showLoader?: boolean
}

const AuthButton: React.FC<Props> = ({
  children,
  className,
  size = 'lg',
  isGoogleAuth,
  providerUserInfo,
  providerData,
  providerId,
  showLoader,
  ...props
}) => {
  const { authUser } = useAuthUser()
  const [logoComponent, setLogoComponent] = useState<React.ReactNode>(null)

  const photoURL =
    authUser?.photoURL || providerData?.find((pd) => pd.photoURL)?.photoURL

  const displayName =
    authUser?.displayName ||
    providerData?.find((pd) => pd.displayName)?.displayName

  useEffect(() => {
    switch (providerId) {
      case GoogleAuthProvider.PROVIDER_ID:
        setLogoComponent(
          <div className='inline-flex'>
            <GoogleIcon width={size === 'lg' ? 30 : size === 'sm' ? 15 : 22} />
          </div>
        )
        break
      case AppleProvider.PROVIDER_ID:
        setLogoComponent(
          <div className='inline-flex'>
            <AppleIcon width={size === 'lg' ? 30 : size === 'sm' ? 15 : 22} />
          </div>
        )
        break
      case EmailAuthProvider.PROVIDER_ID:
        setLogoComponent(
          <div className='inline-flex'>
            <EnvelopeIcon
              width={size === 'lg' ? 30 : size === 'sm' ? 15 : 22}
            />
          </div>
        )
        break
      default:
        break
    }
  }, [providerId, size])

  return (
    <button
      className={cn(
        'relative flex w-full items-center justify-center rounded-full border border-black/10 text-left outline-none transition-all hover:bg-black hover:text-white dark:bg-black',
        className
      )}
      {...props}
    >
      {!authUser || authUser?.isAnonymous || isGoogleAuth ? (
        <div
          className={cn(
            'relative flex flex-shrink-0 flex-col items-center justify-center',
            {
              'h-8 w-8 ': size === 'sm',
              'h-12 w-12': size === 'md',
              'h-16 w-16': size === 'lg',
            }
          )}
        >
          {providerUserInfo ? (
            photoURL ? (
              <div
                style={{
                  backgroundImage: `url(${photoURL})`,
                }}
                className={cn('h-[38px] w-[38px] rounded-full bg-cover', {
                  'h-[28px] w-[28px]': size === 'sm',
                  'h-[32px] w-[32px]': size === 'md',
                  'h-[38px] w-[38px]': size === 'lg',
                })}
              />
            ) : (
              <div className='rounded-full bg-gray-100 p-2'>
                <UserIcon className='h-full w-full text-gray-700' />
              </div>
            )
          ) : (
            logoComponent
          )}
        </div>
      ) : (
        <div
          className={cn('', {
            'h-8 w-8 ': size === 'sm',
            'h-12 w-12': size === 'md',
            'h-16 w-16': size === 'lg',
          })}
        />
      )}

      {(children || providerUserInfo) && (
        <div
          className={cn('w-full whitespace-nowrap px-1 text-center', {
            'text-center leading-none': providerUserInfo,
          })}
        >
          {providerUserInfo ? (
            <div className='flex flex-col items-center'>
              <div className='truncate font-semibold'>{displayName}</div>
              <div className='truncate text-sm leading-tight opacity-70'>
                {providerUserInfo.email || providerUserInfo.phoneNumber}
              </div>
            </div>
          ) : (
            <span className='truncate font-medium md:text-lg'>{children}</span>
          )}
        </div>
      )}

      <div
        className={cn(
          'relative flex flex-shrink-0 flex-col items-center justify-center',
          {
            'h-8 w-8 ': size === 'sm',
            'h-12 w-12': size === 'md',
            'h-16 w-16': size === 'lg',
          }
        )}
      >
        {providerUserInfo && logoComponent}
        {showLoader && (
          <div
            className={cn('flex items-center justify-center', {
              'h-6 w-6 ': size === 'sm',
              'h-8 w-8': size === 'md',
              'h-10 w-10': size === 'lg',
            })}
          >
            <div className='h-full w-full animate-spin'>
              <LoaderIcon />
            </div>
          </div>
        )}
      </div>
    </button>
  )
}

export default AuthButton
